import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaSignOutAlt } from "react-icons/fa";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { VOLATILITY_SYMBOLS, fetchVolatilitySymbols, subscribeToTicks } from "../utils/deriv";

const scaleConfidence = (raw) => Math.round(85 + (raw / 100) * 14);

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [volatilities, setVolatilities] = useState(VOLATILITY_SYMBOLS);
  const [ticks, setTicks] = useState({});
  const [signals, setSignals] = useState({});
  const [phase, setPhase] = useState("countdown");
  const [timer, setTimer] = useState(15);

  const ticksRef = useRef({});

  useEffect(() => {
    ticksRef.current = ticks;
  }, [ticks]);

  // Auth
  useEffect(() => {
    const loginid = localStorage.getItem("currentDerivLogin");
    if (!loginid) {
      navigate("/login");
      return;
    }

    const q = query(collection(db, "deriv_users"), where("loginid", "==", loginid));
    getDocs(q)
      .then((snapshot) => {
        if (snapshot.empty) {
          localStorage.removeItem("currentDerivLogin");
          navigate("/login");
          return;
        }
        const data = snapshot.docs[0].data();
        if (data.approved !== true) {
          navigate("/pending");
          return;
        }
        setUser(data);
      })
      .catch(() => {
        localStorage.removeItem("currentDerivLogin");
        navigate("/login");
      });
  }, [navigate]);

  // Live market list
  useEffect(() => {
    let cancelled = false;
    fetchVolatilitySymbols().then((symbols) => {
      if (!cancelled) setVolatilities(symbols);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // WebSockets
  useEffect(() => {
    const sockets = volatilities.map(({ symbol }) =>
      subscribeToTicks(symbol, (quote) => {
        const digit = Math.floor(quote * 10) % 10;
        setTicks((prev) => ({
          ...prev,
          [symbol]: [...(prev[symbol] || []), digit].slice(-50),
        }));
      })
    );

    return () => sockets.forEach((ws) => ws.close());
  }, [volatilities]);

  // Timer Cycle
  useEffect(() => {
    const generateAllSignals = () => {
      volatilities.forEach(({ symbol }) => {
        const history = ticksRef.current[symbol];
        if (!history || history.length < 10) return;

        const recent = history.slice(-50);
        const freq = Array(10).fill(0);
        recent.forEach((d) => freq[d]++);

        let maxCount = 0;
        let bestDigit = 0;

        freq.forEach((count, digit) => {
          if (count > maxCount) {
            maxCount = count;
            bestDigit = digit;
          }
        });

        const dominance = maxCount / recent.length;
        const confidence = scaleConfidence(dominance * 100);
        const evenOdd = bestDigit % 2 === 0 ? "EVEN" : "ODD";

        setSignals((prev) => ({
          ...prev,
          [symbol]: { digit: bestDigit, confidence, evenOdd },
        }));
      });
    };

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          if (phase === "countdown") {
            setPhase("entry");
            generateAllSignals();
            return 5;
          }
          if (phase === "entry") {
            setPhase("trade");
            return 5;
          }
          if (phase === "trade") {
            setPhase("countdown");
            return 15;
          }
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [phase, volatilities]);

  const handleLogout = () => {
    localStorage.removeItem("currentDerivLogin");
    navigate("/login");
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center text-gray-400">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-700 border-t-cyan-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p>Aligning with the Guru engine...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712] text-white flex flex-col">
      {/* Header */}
      <header className="bg-[#0a1325] border-b border-cyan-900/40 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
          <h1 className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
            MatchesGuru
          </h1>

          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-400 hidden sm:block">
              {user.name || "Trader"}
            </span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded text-sm"
            >
              <FaSignOutAlt /> Exit Dojo
            </button>
          </div>
        </div>
      </header>

      {/* Headline */}
      <div className="text-center py-6 px-4">
        <h2 className="text-2xl sm:text-3xl font-bold text-cyan-300">
          Precision Windows Activated
        </h2>
        <p className="text-gray-400 mt-1 text-base sm:text-lg">
          Real-time digit dominance • Enter with timing discipline
        </p>
      </div>

      {/* Grid */}
      <main className="flex-1 px-4 sm:px-6 pb-12 max-w-7xl mx-auto w-full">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {volatilities.map(({ name, symbol }) => {
            const signal = signals[symbol] || {};
            const showEntry = phase === "entry" && signal.digit !== undefined;
            const showTrade = phase === "trade" && signal.digit !== undefined;

            return (
              <div
                key={symbol}
                className="bg-[#0a1325]/80 border border-cyan-900/50 rounded-2xl p-4 flex flex-col items-center text-center"
              >
                <h3 className="text-sm font-semibold text-cyan-300 mb-3 truncate w-full">
                  {name}
                </h3>

                {showEntry || showTrade ? (
                  <>
                    <div
                      className={`w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center text-2xl md:text-3xl font-extrabold mb-2 ${
                        signal.digit % 2 === 0
                          ? "bg-gradient-to-tr from-cyan-500 to-blue-500"
                          : "bg-gradient-to-tr from-indigo-600 to-purple-500"
                      }`}
                    >
                      {signal.digit}
                    </div>

                    <p className="text-yellow-300 font-semibold">
                      {signal.confidence}%
                    </p>

                    <p className="text-xs text-gray-300 mb-1">
                      {signal.evenOdd}
                    </p>

                    {showEntry ? (
                      <p className="text-cyan-300 font-medium">
                        ENTRY in {timer}s
                      </p>
                    ) : (
                      <p className="text-green-400 font-bold">TRADE</p>
                    )}
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#050814] border border-cyan-900/40 flex items-center justify-center text-xl font-bold text-gray-400 mb-2">
                      {timer}s
                    </div>
                    <p className="text-xs text-gray-400">
                      Awaiting next precision window
                    </p>
                  </>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-center text-sm text-gray-500">
          Trade disciplined. Protect capital. Follow timing.
        </div>
      </main>

      <footer className="py-6 text-center text-xs text-gray-600 border-t border-gray-800 mt-auto">
        © 2026 MatchesGuru • Trading involves significant risk
      </footer>
    </div>
  );
}